<template>
  <div class="ai-assistant-container" v-resize="checkMobile">
    <div class="ai-layout" :class="{ 'mobile-layout': isMobile }">
      <!-- Conversations sidebar -->
      <div
        class="conversations-sidebar"
        :class="{ 'sidebar-hidden': isMobile && showChat }"
      >
        <v-toolbar flat dense :color="$vuetify.theme.dark ? 'grey darken-4' : 'primary'" dark>
          <v-btn v-if="isMobile" icon @click="$router.go(-1)">
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>
          <v-icon class="mr-2">mdi-robot-excited</v-icon>
          <v-toolbar-title>{{ $t("ai_assistant.title") }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon @click="createNewConversation" :title="$t('ai_assistant.new_chat')" :loading="isCreating">
            <v-icon>mdi-plus</v-icon>
          </v-btn>
        </v-toolbar>

        <div class="conversations-list">
          <!-- Skeleton loader for conversations -->
          <template v-if="isLoadingConversations">
            <div v-for="i in 5" :key="'skeleton-' + i" class="conversation-item-skeleton">
              <v-skeleton-loader type="list-item-avatar-two-line" />
            </div>
          </template>

          <!-- Conversations list -->
          <template v-else-if="conversations.length > 0">
            <div
              v-for="conv in conversations"
              :key="conv.id"
              class="conversation-item"
              :class="{ 'active': conversation && conversation.id === conv.id }"
              @click="selectConversation(conv)"
            >
              <v-avatar size="40" class="mr-3" :color="$vuetify.theme.dark ? 'grey darken-2' : 'primary lighten-4'">
                <v-icon :color="$vuetify.theme.dark ? 'white' : 'primary'">mdi-robot</v-icon>
              </v-avatar>
              <div class="conversation-info">
                <div class="conversation-title">{{ conv.title || $t('ai_assistant.new_chat') }}</div>
                <div class="conversation-preview">{{ getPreview(conv) }}</div>
              </div>
              <div class="conversation-meta">
                <div class="conversation-date">{{ formatDate(conv.updated_at) }}</div>
                <v-btn
                  icon
                  x-small
                  class="delete-btn"
                  @click.stop="deleteConversation(conv)"
                >
                  <v-icon small>mdi-delete-outline</v-icon>
                </v-btn>
              </div>
            </div>
          </template>

          <!-- Empty state -->
          <div v-else class="no-conversations">
            <v-icon size="48" color="grey lighten-1" class="mb-2">mdi-chat-outline</v-icon>
            <p>{{ $t('ai_assistant.no_conversations') }}</p>
            <v-btn color="primary" @click="createNewConversation">
              <v-icon left>mdi-plus</v-icon>
              {{ $t('ai_assistant.new_chat') }}
            </v-btn>
          </div>
        </div>
      </div>

      <!-- Chat area -->
      <div
        class="chat-area"
        :class="{ 'chat-hidden': isMobile && !showChat }"
      >
        <v-toolbar flat dense :color="$vuetify.theme.dark ? 'grey darken-4' : 'primary'" dark>
          <v-btn v-if="isMobile" icon @click="showChat = false">
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>

          <template v-if="conversation">
            <v-toolbar-title class="text-truncate">
              {{ conversation.title || $t('ai_assistant.new_chat') }}
            </v-toolbar-title>
          </template>
          <template v-else>
            <v-toolbar-title>{{ $t('ai_assistant.select_chat') }}</v-toolbar-title>
          </template>

          <div
            v-if="conversation"
            :class="'pulse mt-3 mx-2 ' + (connected ? 'pulse_success' : 'pulse_error')"
            v-tooltip="{
              content: connected ? $t('custom.connected') : $t('custom.disconnected'),
              placement: 'bottom-center',
              classes: ['info'],
              targetClasses: ['it-has-a-tooltip'],
              delay: { show: 500, hide: 500 },
            }"
          ></div>

          <v-spacer></v-spacer>

          <!-- Model selector -->
          <!-- <v-menu offset-y v-if="conversation">
            <template v-slot:activator="{ on, attrs }">
              <v-btn text small v-bind="attrs" v-on="on" class="mr-2">
                <v-icon left small>mdi-chip</v-icon>
                <span v-if="!isMobile">{{ currentModelName }}</span>
                <v-icon right small>mdi-chevron-down</v-icon>
              </v-btn>
            </template>
            <v-list dense>
              <v-list-item
                v-for="model in availableModels"
                :key="model.id"
                @click="changeModel(model.id)"
              >
                <v-list-item-title>{{ model.name }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu> -->
        </v-toolbar>

        <!-- No conversation selected -->
        <div v-if="!conversation" class="no-chat-selected">
          <v-icon size="80" color="grey lighten-1" class="mb-4">mdi-chat-processing-outline</v-icon>
          <h3>{{ $t('ai_assistant.select_or_create') }}</h3>
          <v-btn color="primary" class="mt-4" @click="createNewConversation">
            <v-icon left>mdi-plus</v-icon>
            {{ $t('ai_assistant.new_chat') }}
          </v-btn>
        </div>

        <!-- Messages container -->
        <div
          v-else
          ref="messagesContainer"
          class="messages-container"
        >
          <!-- Skeleton loader for messages -->
          <div v-if="isLoadingMessages" class="messages-skeleton">
            <div class="message-skeleton assistant">
              <v-skeleton-loader type="avatar" class="mr-2" />
              <v-skeleton-loader type="paragraph" width="60%" />
            </div>
            <div class="message-skeleton user">
              <v-skeleton-loader type="paragraph" width="40%" />
              <v-skeleton-loader type="avatar" class="ml-2" />
            </div>
            <div class="message-skeleton assistant">
              <v-skeleton-loader type="avatar" class="mr-2" />
              <v-skeleton-loader type="paragraph" width="70%" />
            </div>
          </div>

          <!-- Welcome message when no messages -->
          <div v-else-if="messages.length === 0 && !isLoadingMessages" class="welcome-message text-center">
            <v-icon size="64" color="primary" class="mb-4">mdi-robot-excited</v-icon>
            <h2 class="text-h5 mb-2">{{ $t("ai_assistant.welcome_title") }}</h2>
            <p class="text--secondary mb-4">{{ $t("ai_assistant.welcome_subtitle") }}</p>
            
            <!-- Quick actions -->
            <div class="quick-actions d-flex flex-wrap justify-center">
              <v-chip
                v-for="action in quickActions"
                :key="action.key"
                class="ma-1"
                outlined
                @click="sendQuickAction(action.message)"
              >
                <v-icon left small>{{ action.icon }}</v-icon>
                {{ action.label }}
              </v-chip>
            </div>

            <!-- Disclaimer -->
            <p class="disclaimer text-caption grey--text mt-4">
              <v-icon x-small class="mr-1">mdi-information-outline</v-icon>
              {{ $t("ai_assistant.disclaimer") }}
            </p>
          </div>

          <!-- Messages list -->
          <div v-else class="messages-list">
            <div
              v-for="(message, index) in messages"
              :key="message.id || index"
              class="message-wrapper"
              :class="message.role"
            >
              <!-- Операция Ren (FT-199): карточка с кнопками вместо пузыря -->
              <div v-if="message.role === 'action' && message.operation" class="action-card-wrapper">
                <div
                  v-if="batch.firstMessageId === message.id"
                  class="action-batch mb-2"
                  data-testid="ai-action-batch"
                >
                  <v-btn
                    small
                    depressed
                    color="primary"
                    :loading="isBatchDeciding"
                    data-testid="ai-action-batch-approve"
                    @click="approveBatch"
                  >
                    {{ $t('ai_assistant.action_card.approve_all', { count: batch.ids.length }) }}
                  </v-btn>
                </div>
                <ai-action-card
                  :operation="message.operation"
                  :disabled="isBatchDeciding"
                  @refresh="refreshMessages"
                />
              </div>
              <div v-else class="message-bubble" :class="message.role">
                <!-- Avatar -->
                <v-avatar size="32" class="message-avatar" :class="message.role">
                  <v-icon v-if="message.role === 'assistant'" color="white" small>
                    mdi-robot
                  </v-icon>
                  <template v-else>
                    <v-img
                      v-if="currentUser && currentUser.avatar_url"
                      :src="currentUser.avatar_url"
                      :alt="currentUser.name || ''"
                    ></v-img>
                    <span v-else class="white--text">
                      {{ currentUser && currentUser.name ? currentUser.name[0] : '' }}
                    </span>
                  </template>
                </v-avatar>

                <!-- Content -->
                <div class="message-content">
                  <div
                    class="message-text"
                    v-html="formatMessage(message.content)"
                  ></div>
                  <div class="message-time">
                    {{ formatTime(message.created_at) }}
                  </div>
                </div>
              </div>
            </div>

            <!-- Streaming response -->
            <div v-if="isStreaming && streamingContent" class="message-wrapper assistant">
              <div class="message-bubble assistant streaming">
                <v-avatar size="32" class="message-avatar assistant">
                  <v-icon color="white" small>mdi-robot</v-icon>
                </v-avatar>
                <div class="message-content">
                  <div class="message-text" v-html="formatMessage(streamingContent)"></div>
                  <div class="streaming-cursor"></div>
                </div>
              </div>
            </div>

            <!-- Typing indicator -->
            <div v-if="isTyping && !isStreaming" class="message-wrapper assistant">
              <div class="message-bubble assistant typing">
                <v-avatar size="32" class="message-avatar assistant typing-avatar">
                  <v-icon color="white" small>mdi-robot</v-icon>
                </v-avatar>
                <div class="message-content typing-content">
                  <div class="typing-text">
                    <template v-if="currentToolCall">
                      <v-icon small class="mr-1 tool-icon-spin">mdi-database-search</v-icon>
                      {{ currentToolCall.label }}
                    </template>
                    <template v-else>
                      {{ $t('ai_assistant.thinking') }}
                    </template>
                  </div>
                  <div class="typing-indicator">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Input area -->
        <div v-if="conversation" class="input-area pa-3">
          <v-textarea
            v-model="newMessage"
            :placeholder="$t('ai_assistant.input_placeholder')"
            rows="1"
            auto-grow
            max-rows="4"
            outlined
            dense
            hide-details
            class="message-input"
            @keydown.enter.exact.prevent="sendMessage"
            @keydown.enter.shift.exact="newMessage += '\n'"
            :disabled="isTyping"
          ></v-textarea>

          <v-btn
            icon
            color="primary"
            class="ml-2"
            :disabled="!newMessage.trim() || isTyping"
            @click="sendMessage"
          >
            <v-icon>mdi-send</v-icon>
          </v-btn>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import CableSubscriber from '@/mixins/CableSubscriber'
import { isRateLimitedError } from '@/utils/networkErrors'
import { formatChatMessage } from '@/utils/chatMarkdown'
import setErrorMixin from '@/mixins/setErrorMixin'
import AiActionCard from './AiActionCard.vue'
import { API_PATH } from '@/components/agents/agentOperationOutcome'
import { CAPABILITIES, pendingBatch, batchDecidedCount } from './aiActionCard'

// HTTP-отказ, после которого сервер точно ничего не принял (ответ есть и он 4xx).
const isRefusal = (error) => {
  const status = error && error.response && error.response.status
  return status >= 400 && status < 500
}

export default {
  name: 'AiAssistant',

  mixins: [CableSubscriber],

  components: { AiActionCard },

  data () {
    return {
      subscribedConversationId: null,
      isLoadingConversations: false,
      isLoadingMessages: false,
      isCreating: false,
      isTyping: false,
      isStreaming: false,
      streamingContent: '',
      currentToolCall: null, // Текущий вызов инструмента { tool: 'name', label: 'Поиск...' }
      isMobile: false,
      showChat: false, // для мобильного: показывать чат или список
      connected: false,
      conversations: [],
      conversation: null,
      messages: [],
      // Перечитывание беседы (FT-199): запрос в полёте / ещё одно ждёт своей очереди / номер последнего запроса
      isRefreshing: false,
      refreshQueued: false,
      messagesSeq: 0, // номер последнего запроса сообщений
      messagesAppliedSeq: 0, // номер запроса, чей ответ уже в messages: более старый ответ не затирает новый
      messagesLoadingSeq: 0, // номер последнего НЕтихого запроса — он гасит скелетон
      isBatchDeciding: false,
      newMessage: '',
      selectedModel: 'deepseek-chat',
      availableModels: [
        { id: 'deepseek-chat', name: 'DeepSeek Chat', provider: 'deepseek' },
        { id: 'deepseek-reasoner', name: 'DeepSeek Reasoner', provider: 'deepseek' },
        // { id: 'gpt-4o-mini', name: 'GPT-4o Mini', provider: 'openai' },
        // { id: 'gpt-4o', name: 'GPT-4o', provider: 'openai' },
        // { id: 'claude-3-5-sonnet-20241022', name: 'Claude 3.5 Sonnet', provider: 'anthropic' }
      ]
    }
  },

  computed: {
    currentModelName () {
      const model = this.availableModels.find(m => m.id === this.selectedModel)
      return model ? model.name : this.selectedModel
    },

    currentUser () {
      return this.$store.getters.getCurrentUser
    },

    // «Выполнить все»: ждущие карточки последнего хода, если их две и больше
    batch () {
      return pendingBatch(this.messages)
    },

    quickActions () {
      return [
        {
          key: 'active_bookings',
          icon: 'mdi-book-multiple',
          label: this.$t('ai_assistant.quick_active_bookings'),
          message: this.$t('ai_assistant.quick_active_bookings_msg')
        },
        {
          key: 'today_schedule',
          icon: 'mdi-calendar-today',
          label: this.$t('ai_assistant.quick_today'),
          message: this.$t('ai_assistant.quick_today_msg')
        },
        {
          key: 'available_cars',
          icon: 'mdi-check-circle',
          label: this.$t('ai_assistant.quick_available_cars'),
          message: this.$t('ai_assistant.quick_available_cars_msg')
        },
        {
          key: 'available_dates',
          icon: 'mdi-calendar-check',
          label: this.$t('ai_assistant.quick_available_dates'),
          message: this.$t('ai_assistant.quick_available_dates_msg')
        },
        {
          key: 'statistics',
          icon: 'mdi-chart-bar',
          label: this.$t('ai_assistant.quick_statistics'),
          message: this.$t('ai_assistant.quick_statistics_msg')
        },
        {
          key: 'conversion',
          icon: 'mdi-percent',
          label: this.$t('ai_assistant.quick_conversion'),
          message: this.$t('ai_assistant.quick_conversion_msg')
        },
        {
          key: 'overdue',
          icon: 'mdi-clock-alert',
          label: this.$t('ai_assistant.quick_overdue'),
          message: this.$t('ai_assistant.quick_overdue_msg')
        },
        {
          key: 'debtors',
          icon: 'mdi-account-alert',
          label: this.$t('ai_assistant.quick_clients_debtors'),
          message: this.$t('ai_assistant.quick_clients_debtors_msg')
        },
        {
          key: 'fleet_stats',
          icon: 'mdi-car-multiple',
          label: this.$t('ai_assistant.quick_fleet_stats'),
          message: this.$t('ai_assistant.quick_fleet_stats_msg')
        },
        {
          key: 'cars_profit',
          icon: 'mdi-cash-multiple',
          label: this.$t('ai_assistant.quick_cars_profit'),
          message: this.$t('ai_assistant.quick_cars_profit_msg')
        },
        {
          key: 'cars_occupancy',
          icon: 'mdi-chart-donut',
          label: this.$t('ai_assistant.quick_cars_occupancy'),
          message: this.$t('ai_assistant.quick_cars_occupancy_msg')
        },
        {
          key: 'maintenance',
          icon: 'mdi-wrench-clock',
          label: this.$t('ai_assistant.quick_maintenance'),
          message: this.$t('ai_assistant.quick_maintenance_msg')
        },
        {
          key: 'expiring_insurance',
          icon: 'mdi-shield-alert',
          label: this.$t('ai_assistant.quick_expiring_insurance'),
          message: this.$t('ai_assistant.quick_expiring_insurance_msg')
        },
        {
          key: 'available_for_service',
          icon: 'mdi-tools',
          label: this.$t('ai_assistant.quick_available_for_service'),
          message: this.$t('ai_assistant.quick_available_for_service_msg')
        },
        // Help section
        {
          key: 'help',
          icon: 'mdi-help-circle-outline',
          label: this.$t('ai_assistant.quick_help'),
          message: this.$t('ai_assistant.quick_help_msg')
        },
        {
          key: 'help_booking',
          icon: 'mdi-book-plus',
          label: this.$t('ai_assistant.quick_help_booking'),
          message: this.$t('ai_assistant.quick_help_booking_msg')
        },
        {
          key: 'help_prices',
          icon: 'mdi-currency-usd',
          label: this.$t('ai_assistant.quick_help_prices'),
          message: this.$t('ai_assistant.quick_help_prices_msg')
        }
      ]
    }
  },

  async mounted () {
    this.checkMobile()
    await this.loadConversations()
  },

  beforeDestroy () {
    this.unsubscribeFromChannel()
  },

  methods: {
    checkMobile () {
      this.isMobile = window.innerWidth < 960
    },

    // Свой setError — ради одного случая: лимит сообщений самого чата
    // (ai_chat_controller кладёт retry_after в тело) объясняется своим текстом.
    // Всё прочее — глобальный setError (setErrorMixin): Rollbar, warning на
    // ожидаемые отказы и 429 от Rack::Attack, без сырого текста сервера.
    setError (error, defaultMessage) {
      const chatRetryAfter = isRateLimitedError(error) && error.response.data && error.response.data.retry_after
      if (chatRetryAfter) {
        const message = this.$t('ai_assistant.rate_limit_retry', { minutes: Math.ceil(chatRetryAfter / 60) })
        this.$notify(this.$t('ai_assistant.rate_limit_title'), message, 'warning', 6000)
        return
      }
      setErrorMixin.methods.setError.call(this, error, defaultMessage)
    },

    async loadConversations () {
      this.isLoadingConversations = true

      this.axios
        .get('/api/v1/ai_chat/conversations', {
          headers: {
            Authorization: this.$store.getters.getAuthToken
          }
        })
        .then((response) => {
          this.conversations = response.data.conversations || []

          // Автоматически выбираем первую беседу на десктопе
          if (!this.isMobile && this.conversations.length > 0) {
            this.selectConversation(this.conversations[0])
          }
        })
        .catch((error) => {
          this.setError(error, this.$t('ai_assistant.error_init'))
        })
        .finally(() => {
          this.isLoadingConversations = false
        })
    },

    selectConversation (conv) {
      this.unsubscribeFromChannel()
      this.conversation = conv
      this.messages = []
      this.showChat = true
      this.messagesAppliedSeq = this.messagesSeq

      this.loadMessages()
      this.subscribeToChannel()
    },

    loadMessages ({ silent = false } = {}) {
      if (!this.conversation) return Promise.resolve()

      const conversationId = this.conversation.id
      const seq = ++this.messagesSeq
      if (!silent) {
        this.isLoadingMessages = true
        this.messagesLoadingSeq = seq
      }

      // capabilities: action_cards — без него в ответе нет сообщений role: "action" (карточек операций Ren)
      return this.axios
        .get(`/api/v1/ai_chat/conversations/${conversationId}`, {
          params: { capabilities: CAPABILITIES.join(',') },
          headers: {
            Authorization: this.$store.getters.getAuthToken
          }
        })
        .then((response) => {
          // ответ устарел: беседу сменили или уже применён ответ более нового запроса
          if (seq < this.messagesAppliedSeq || !this.conversation || this.conversation.id !== conversationId) return
          this.messagesAppliedSeq = seq
          const previousCount = this.messages.length
          this.messages = this.mergeServerMessages(response.data.messages || [])
          if (silent && this.messages.length > previousCount) this.scrollToBottom()
        })
        .catch((error) => {
          // тихое перечитывание (опрос статуса) не должно сыпать тостами; первичная загрузка — как раньше
          if (!silent) this.setError(error, this.$t('ai_assistant.error_init'))
        })
        .finally(() => {
          if (!silent && seq === this.messagesLoadingSeq) {
            this.isLoadingMessages = false
            // Скролл после того как skeleton скрылся и сообщения отрисовались
            this.$nextTick(() => {
              this.scrollToBottom()
            })
          }
        })
    },

    // Перечитать сообщения беседы без скелетона: карточки операций (появление по каналу, смена статуса, опрос).
    // Запросы не копятся: пока один в полёте, остальные схлопываются в один повтор после него.
    refreshMessages () {
      if (!this.conversation) return Promise.resolve()
      if (this.isRefreshing) {
        this.refreshQueued = true
        return Promise.resolve()
      }
      this.isRefreshing = true
      return this.loadMessages({ silent: true }).finally(() => {
        this.isRefreshing = false
        if (this.refreshQueued) {
          this.refreshQueued = false
          this.refreshMessages()
        }
      })
    },

    // Ответ HTTP целиком заменяет messages, но два вида сообщений он может ещё не содержать:
    //  - пришедшие по каналу (live: true) — сервер мог не успеть записать их к моменту запроса; живут, пока их id не
    //    появится в ответе;
    //  - сообщение пользователя, ещё не записанное сервером (запись в ProcessAiMessageJob; local: true). Оно считается
    //    записанным, когда серверных сообщений с тем же текстом стало больше, чем было известно при отправке
    //    (sentAfterCount): одинаковый короткий текст («да», «ок») в прошлых ходах его не «записывает». Несколько
    //    неотправленных с одним текстом занимают записи по очереди.
    mergeServerMessages (serverMessages) {
      const serverIds = new Set(serverMessages.map(m => m.id))
      const live = this.messages.filter(m => m.live && !serverIds.has(m.id))
      const seen = {}
      const unsent = this.messages.filter(m => {
        if (!m.local) return false
        const rank = seen[m.content] = (seen[m.content] || 0) + 1
        const saved = serverMessages.filter(s => s.role === 'user' && s.content === m.content).length
        return saved < (m.sentAfterCount || 0) + rank
      })
      return live.length || unsent.length ? [...serverMessages, ...live, ...unsent] : serverMessages
    },

    // «Выполнить все»: одна пачка на ждущие карточки хода; результат каждой — в её статусе после перечитывания
    async approveBatch () {
      const ids = this.batch.ids
      if (this.isBatchDeciding || ids.length === 0) return
      this.isBatchDeciding = true
      try {
        const { data } = await this.axios.post(`${API_PATH}/batch`, { ids, decision: 'approve' }, {
          headers: {
            Authorization: this.$store.getters.getAuthToken
          }
        })
        const decided = batchDecidedCount(data && data.data)
        if (decided < ids.length) {
          this.$notify(
            this.$t('ai_assistant.action_card.batch_summary', { ok: decided, total: ids.length }),
            '',
            'warning'
          )
        }
      } catch (error) {
        this.$notify(this.$t('agent_operations.outcome_decision_error'), '', 'error')
      } finally {
        this.isBatchDeciding = false
        this.refreshMessages()
      }
    },

    createNewConversation () {
      this.isCreating = true
      this.unsubscribeFromChannel()

      this.axios
        .post('/api/v1/ai_chat/conversations', {
          model_id: this.selectedModel
        }, {
          headers: {
            Authorization: this.$store.getters.getAuthToken
          }
        })
        .then((response) => {
          const newConv = response.data.conversation
          this.conversations.unshift(newConv)
          this.conversation = newConv
          this.messages = []
          this.showChat = true
          this.subscribeToChannel()
        })
        .catch((error) => {
          this.setError(error, this.$t('ai_assistant.error_create'))
        })
        .finally(() => {
          this.isCreating = false
        })
    },

    deleteConversation (conv) {
      if (!confirm(this.$t('ai_assistant.delete_confirm'))) return

      this.axios
        .delete(`/api/v1/ai_chat/conversations/${conv.id}`, {
          headers: {
            Authorization: this.$store.getters.getAuthToken
          }
        })
        .then(() => {
          const index = this.conversations.findIndex(c => c.id === conv.id)
          if (index !== -1) {
            this.conversations.splice(index, 1)
          }

          if (this.conversation && this.conversation.id === conv.id) {
            this.unsubscribeFromChannel()
            this.conversation = null
            this.messages = []
            this.showChat = false
          }
        })
        .catch((error) => {
          this.setError(error, this.$t('ai_assistant.error_delete'))
        })
    },

    onAiChatConnected () {
      console.log('[AiChat] Connected to channel')
      this.connected = true
    },

    onAiChatDisconnected () {
      console.log('[AiChat] Disconnected from channel')
      this.connected = false
    },

    subscribeToChannel () {
      if (!this.conversation) return
      // Same-conversation no-op — already subscribed to this AiChatChannel
      if (this.aiChatSub && this.subscribedConversationId === this.conversation.id) return
      // Switch: unsubscribe the previous conversation (held ref) before subscribing the new one
      if (this.aiChatSub) {
        this.$cableUnsubscribe(this.aiChatSub)
        this.aiChatSub = null
      }
      this.subscribedConversationId = this.conversation.id
      this.aiChatSub = this.$cableSubscribe(
        {
          channel: 'AiChatChannel',
          room: this.subscribedConversationId,
          user_id: this.$store.getters.getCurrentUser.id
        },
        {
          connected: () => this.onAiChatConnected(),
          disconnected: () => this.onAiChatDisconnected(),
          received: (data) => this.handleChannelMessage(data)
        }
      )
    },

    unsubscribeFromChannel () {
      if (this.aiChatSub) {
        this.$cableUnsubscribe(this.aiChatSub)
        this.aiChatSub = null
      }
      this.subscribedConversationId = null
    },

    handleChannelMessage (data) {
      // Streaming chunk - добавляем текст по мере генерации
      if (data.type === 'chunk') {
        if (!this.isStreaming) {
          this.isStreaming = true
          this.isTyping = false
          this.streamingContent = ''
        }
        this.streamingContent += data.content
        this.scrollToBottom()
        return
      }

      // Stream завершён
      if (data.type === 'stream_end') {
        this.isStreaming = false
        return
      }

      // Финальное сообщение с ID
      if (data.type === 'message' && data.message) {
        // Очищаем streaming контент и tool call
        this.isStreaming = false
        this.streamingContent = ''
        this.currentToolCall = null

        const exists = this.messages.some(m => m.id === data.message.id)
        if (!exists) {
          // live: перечитывание, начатое до записи этого сообщения, его не затрёт (mergeServerMessages)
          this.messages.push({ ...data.message, live: true })
          this.scrollToBottom()

          // Обновляем заголовок беседы
          if (this.conversation && data.message.role === 'assistant') {
            this.updateConversationInList()
          }
        }
        this.isTyping = false
        return
      }

      // Карточка операции Ren (FT-199): канал несёт только факт — id операции и инструмент, без данных
      // (CABLE_AUTH_ENFORCE=false: в канале не должно быть содержимого). Саму карточку берём из HTTP.
      if (data.type === 'action') {
        this.refreshMessages()
        return
      }

      // Tool call - показываем какой инструмент используется
      if (data.type === 'tool_call') {
        this.currentToolCall = {
          tool: data.tool,
          label: data.label
        }
        return
      }

      // Ошибка
      if (data.type === 'error') {
        this.isTyping = false
        this.isStreaming = false
        this.streamingContent = ''
        this.currentToolCall = null
        this.$notify(this.$t('errors.error'), data.error, 'error', 5000)
      }
    },

    updateConversationInList () {
      // Перезагружаем беседу для обновления заголовка
      this.axios
        .get(`/api/v1/ai_chat/conversations/${this.conversation.id}`, {
          headers: {
            Authorization: this.$store.getters.getAuthToken
          }
        })
        .then((response) => {
          const updatedConv = response.data.conversation
          if (updatedConv) {
            this.conversation.title = updatedConv.title
            const index = this.conversations.findIndex(c => c.id === this.conversation.id)
            if (index !== -1) {
              this.$set(this.conversations, index, { ...this.conversations[index], title: updatedConv.title })
            }
          }
        })
        .catch(() => {})
    },

    sendMessage () {
      const message = this.newMessage.trim()
      if (!message || this.isTyping) return

      const pending = {
        id: Date.now(),
        role: 'user',
        content: message,
        created_at: new Date().toISOString(),
        local: true,
        // сколько сообщений пользователя с таким текстом сервер уже знал к отправке (см. mergeServerMessages)
        sentAfterCount: this.messages.filter(m => m.role === 'user' && !m.local && m.content === message).length
      }
      this.messages.push(pending)

      this.newMessage = ''
      this.isTyping = true
      this.scrollToBottom()

      this.axios
        .post(`/api/v1/ai_chat/conversations/${this.conversation.id}/ask`, {
          message: message,
          model_id: this.selectedModel,
          capabilities: CAPABILITIES
        }, {
          headers: {
            Authorization: this.$store.getters.getAuthToken
          }
        })
        .then(() => {})
        .catch((error) => {
          this.isTyping = false
          if (isRefusal(error)) this.dropUnsent(pending)
          this.setError(error, this.$t('ai_assistant.error_send'))
        })
    },

    // Однозначный отказ (4xx: лимит, нет доступа, пустое сообщение) — сервер сообщение не принял и не запишет.
    // Временное сообщение убираем: иначе оно навсегда встаёт после серверной истории (mergeServerMessages) и
    // pendingBatch считает его последним ходом — «Выполнить все» пропадает. Текст — обратно в поле, если оно пустое.
    // Сбой сети без ответа и 5xx не однозначны (запрос мог дойти) — сообщение остаётся.
    dropUnsent (pending) {
      this.messages = this.messages.filter(m => m !== pending)
      if (!this.newMessage.trim()) this.newMessage = pending.content
    },

    sendQuickAction (message) {
      this.newMessage = message
      this.sendMessage()
    },

    changeModel (modelId) {
      this.selectedModel = modelId
    },

    getPreview (conv) {
      return conv.last_message || this.$t('ai_assistant.no_messages')
    },

    formatDate (dateStr) {
      if (!dateStr) return ''
      const date = moment(dateStr)
      if (date.isSame(moment(), 'day')) {
        return date.format('HH:mm')
      } else if (date.isSame(moment(), 'year')) {
        return date.format('DD MMM')
      }
      return date.format('DD.MM.YY')
    },

    formatMessage (content) {
      return formatChatMessage(content)
    },

    formatTime (dateStr) {
      if (!dateStr) return ''
      const date = new Date(dateStr)
      return date.toLocaleTimeString(this.$i18n.locale, {
        hour: '2-digit',
        minute: '2-digit'
      })
    },

    scrollToBottom () {
      this.$nextTick(() => {
        const container = this.$refs.messagesContainer
        if (container) {
          container.scrollTop = container.scrollHeight
        }
      })
    }
  }
}
</script>

<style scoped lang="scss">
.ai-assistant-container {
  height: calc(100vh - 64px);
  overflow: hidden;
}

.ai-layout {
  display: flex;
  height: 100%;

  &.mobile-layout {
    .conversations-sidebar {
      width: 100%;
      position: absolute;
      left: 0;
      top: 0;
      bottom: 0;
      z-index: 1;
      transition: transform 0.3s ease;

      &.sidebar-hidden {
        transform: translateX(-100%);
      }
    }

    .chat-area {
      width: 100%;
      position: absolute;
      left: 0;
      top: 0;
      bottom: 0;
      z-index: 2;
      transition: transform 0.3s ease;

      &.chat-hidden {
        transform: translateX(100%);
      }
    }
  }
}

.conversations-sidebar {
  width: 320px;
  min-width: 320px;
  border-right: 1px solid var(--v-divider-base);
  display: flex;
  flex-direction: column;
  background: var(--v-background-base);

  .v-toolbar {
    flex-shrink: 0;
    flex-grow: 0;
  }
}

.conversations-list {
  flex: 1;
  overflow-y: auto;
}

.conversation-item {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  cursor: pointer;
  border-bottom: 1px solid var(--v-divider-base);
  transition: background 0.2s;

  &:hover {
    background: var(--v-grey-lighten4);
  }

  &.active {
    background: var(--v-primary-lighten5);
  }
}

.conversation-info {
  flex: 1;
  min-width: 0;
}

.conversation-title {
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.conversation-preview {
  font-size: 13px;
  color: var(--v-grey-darken1);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.conversation-meta {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  margin-left: 8px;
}

.conversation-date {
  font-size: 11px;
  color: var(--v-grey-base);
  margin-bottom: 4px;
}

.delete-btn {
  opacity: 0;
  transition: opacity 0.2s;
}

.conversation-item:hover .delete-btn {
  opacity: 1;
}

.no-conversations {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 16px;
  color: var(--v-grey-base);
}

.conversation-item-skeleton {
  padding: 8px 16px;
  border-bottom: 1px solid var(--v-divider-base);
}

.messages-skeleton {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
  margin-top: auto;
}

.message-skeleton {
  display: flex;
  align-items: flex-start;

  &.user {
    justify-content: flex-end;
  }

  &.assistant {
    justify-content: flex-start;
  }
}

.chat-area {
  flex: 1;
  display: flex;
  flex-direction: column;
  background: var(--v-background-base);

  .v-toolbar {
    flex-shrink: 0;
    flex-grow: 0;
  }
}

.no-chat-selected {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: var(--v-grey-base);
}

.messages-container {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
}

.welcome-message {
  margin: auto;
  padding: 48px 16px;

  h2 {
    font-weight: 500;
  }
}

.quick-actions {
  max-width: 500px;
  margin: 0 auto;
}

.messages-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: auto;
}

.message-wrapper {
  display: flex;

  &.user {
    justify-content: flex-end;
  }

  &.assistant,
  &.action {
    justify-content: flex-start;
  }
}

.action-card-wrapper {
  width: 100%;
  max-width: calc(100% - 40px);
  margin-left: 40px; // под аватар ассистента (32px + gap 8px): карточка стоит в колонке его текста
}

.message-bubble {
  display: flex;
  max-width: 80%;
  gap: 8px;

  &.user {
    flex-direction: row-reverse;

    .message-content {
      background: #1976d2;
      color: white;
      border-radius: 16px 16px 4px 16px;
    }

    .message-time {
      text-align: right;
      color: rgba(255, 255, 255, 0.7);
    }
  }

  &.assistant {
    .message-content {
      border-radius: 16px 16px 16px 4px;
    }
  }
}

.message-avatar {
  flex-shrink: 0;

  &.user {
    background: #1565c0 !important;
  }

  &.assistant {
    background: #43a047 !important;
  }
}

.message-content {
  padding: 12px 16px;
  word-break: break-word;

  .message-text {
    line-height: 1.5;

    ::v-deep .list-item {
      display: block;
      margin: 4px 0;
    }

    ::v-deep strong {
      font-weight: 600;
    }

    ::v-deep .chat-link {
      color: #4FC3F7;
      text-decoration: none;
      border-bottom: 1px dotted rgba(79, 195, 247, 0.5);
      transition: all 0.2s ease;

      &:hover {
        color: #81D4FA;
        border-bottom-color: #81D4FA;
        border-bottom-style: solid;
      }
    }
  }

  .message-time {
    font-size: 11px;
    margin-top: 4px;
    opacity: 0.7;
  }
}

.typing-avatar {
  animation: pulse-avatar 2s infinite;
}

.typing-content {
  display: flex;
  align-items: center;
  gap: 8px;
}

.typing-text {
  font-size: 13px;
  font-style: italic;
}

.typing-indicator {
  display: flex;
  gap: 4px;
  padding: 4px 0;

  span {
    width: 6px;
    height: 6px;
    background: var(--v-primary-base);
    border-radius: 50%;
    animation: typing 1.4s infinite ease-in-out both;

    &:nth-child(1) {
      animation-delay: -0.32s;
    }
    &:nth-child(2) {
      animation-delay: -0.16s;
    }
  }
}

.tool-icon-spin {
  animation: pulse 1s ease-in-out infinite;
  color: var(--v-primary-base);
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.4;
  }
}

@keyframes typing {
  0%, 80%, 100% {
    transform: scale(0.6);
    opacity: 0.4;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
}

.streaming-cursor {
  display: inline-block;
  width: 8px;
  height: 16px;
  background: var(--v-primary-base);
  margin-left: 2px;
  animation: blink 1s step-end infinite;
}

@keyframes blink {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0;
  }
}

@keyframes pulse-avatar {
  0%, 100% {
    box-shadow: 0 0 0 0 rgba(76, 175, 80, 0.4);
  }
  50% {
    box-shadow: 0 0 0 8px rgba(76, 175, 80, 0);
  }
}

.input-area {
  display: flex;
  align-items: flex-end;
  border-top: 1px solid var(--v-divider-base);
  background: var(--v-surface-base);
}

.message-input {
  ::v-deep .v-input__slot {
    min-height: 40px !important;
  }
}
</style>

<style lang="scss">
// Theme-specific styles
.theme--light {
  .ai-assistant-container {
    .conversations-sidebar {
      background: #fafafa;
    }

    .conversation-item {
      &:hover {
        background: #f0f0f0;
      }

      &.active {
        background: #e3f2fd;
      }
    }

    .chat-area {
      background: #fff;
    }

    .messages-container {
      background: #f5f5f5;
    }

    .message-bubble.assistant .message-content {
      background: white;
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
      color: #333;
    }

    .typing-text {
      color: #555;
    }

    .welcome-message h2 {
      color: #333;
    }

    .welcome-message p {
      color: #666;
    }

    .chat-link {
      color: #1976D2 !important;
      border-bottom-color: rgba(25, 118, 210, 0.5) !important;

      &:hover {
        color: #1565C0 !important;
        border-bottom-color: #1565C0 !important;
      }
    }
  }
}

.theme--dark {
  .ai-assistant-container {
    .conversations-sidebar {
      background: #1e1e1e;
    }

    .conversation-item {
      &:hover {
        background: #2d2d2d;
      }

      &.active {
        background: #1565c0;
      }
    }

    .chat-area {
      background: #121212;
    }

    .messages-container {
      background: #1e1e1e;
    }

    .message-bubble.assistant .message-content {
      background: #2d2d2d;
      color: #e0e0e0;
    }

    .typing-text {
      color: #aaa;
    }

    .conversation-preview {
      color: #888;
    }
  }
}
</style>
